import { MessageType } from "../types";
import { baseFetch } from "./base";
import { v4 as uuidv4 } from "uuid";

const isSafariBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  return (
    (ua.includes("safari") && !ua.includes("chrome")) ||
    ua.includes("iphone") ||
    ua.includes("ipad")
  );
};

export const buildUploadFile = (file: Blob | File, type: MessageType): File => {
  let extension = "";
  const isSafari = isSafariBrowser();

  if (file instanceof File) {
    extension = file.name.split(".").pop() || "";
  } else {
    const mimeType = file.type.toLowerCase();

    if (type === "audio" && isSafari) {
      extension = "mp3";
    } else if (mimeType.includes("mp3") || mimeType.includes("mpeg")) {
      extension = "mp3";
    } else if (mimeType.includes("mp4") || mimeType.includes("mp4a")) {
      extension = "m4a";
    } else if (mimeType.includes("webm")) {
      extension = "webm";
    } else if (mimeType.includes("ogg")) {
      extension = "ogg";
    } else if (mimeType.includes("wav")) {
      extension = "wav";
    } else if (type === "audio") {
      extension = isSafari ? "mp3" : "webm";
    } else {
      extension = "bin";
    }
  }

  const uniqueFileName = `${uuidv4()}-${Date.now()}.${extension}`;
  let fileType = file.type;
  if (type === "audio" && isSafari) {
    fileType = "audio/mp3";
  }

  return new File([file], uniqueFileName, { type: fileType });
};

const postMultipartFile = async (
  endpoint: string,
  file: File,
  extraFields?: Record<string, string | number>,
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (extraFields) {
    Object.entries(extraFields).forEach(([key, value]) => {
      formData.append(key, String(value));
    });
  }
  return baseFetch.post(endpoint, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadFile = async (file: Blob | File, type: MessageType) => {
  try {
    const fileWithExtension = buildUploadFile(file, type);
    const response = await postMultipartFile("/upload", fileWithExtension);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      console.error("Server responded with:", error.response.data);
      console.error(`Upload failed with status: ${error.response.status}`);
    } else {
      console.error(`Error uploading ${type}:`, error);
      console.error(`Error message: ${error.message}`);
    }
    throw error;
  }
};

export type ExtractTextFromAudioMeta = {
  assessment_id?: string | null;
  question_index?: number | null;
};

export const extractTextFromAudio = async (
  file: Blob | File,
  meta?: ExtractTextFromAudioMeta,
): Promise<string> => {
  try {
    const fileWithExtension = buildUploadFile(file, "audio");
    const assessmentId =
      typeof meta?.assessment_id === "string" ? meta.assessment_id.trim() : "";
    const hasQuestionIndex =
      typeof meta?.question_index === "number" &&
      Number.isFinite(meta.question_index) &&
      meta.question_index >= 0;

    const extraFields: Record<string, string | number> = {};
    if (assessmentId || hasQuestionIndex) {
      extraFields.data = JSON.stringify({
        assessment_id: assessmentId,
        question_index: hasQuestionIndex
          ? String(Math.floor(meta!.question_index!))
          : "",
      });
    }

    const response = await postMultipartFile(
      "/extract_text",
      fileWithExtension,
      Object.keys(extraFields).length > 0 ? extraFields : undefined,
    );
    const data = response.data;
    return typeof data === "string" ? data.trim() : String(data ?? "").trim();
  } catch (error: any) {
    if (error.response) {
      console.error("Server responded with:", error.response.data);
      console.error(
        `Extract text failed with status: ${error.response.status}`,
      );
    } else {
      console.error("Error extracting text from audio:", error);
    }
    throw error;
  }
};
